import React, { useEffect, useState } from 'react';
import { MailIcon, PhoneIcon, ShieldIcon, SendIcon, BuildingIcon, CalendarIcon, UserIcon, CheckIcon, ChevronRightIcon, ShieldCheckIcon } from 'lucide-react';
// Move service types and urgency levels to module scope
export const SERVICE_TYPES = [{
  id: 'objectbeveiliging',
  label: 'Objectbeveiliging',
  icon: <BuildingIcon className="w-5 h-5" data-id="element-79" />
}, {
  id: 'winkelbeveiliging',
  label: 'Winkelbeveiliging',
  icon: <ShieldIcon className="w-5 h-5" data-id="element-80" />
}, {
  id: 'evenementenbeveiliging',
  label: 'Evenementen',
  icon: <CalendarIcon className="w-5 h-5" data-id="element-81" />
}, {
  id: 'anders',
  label: 'Anders',
  icon: <CheckIcon className="w-5 h-5" data-id="element-82" />
}] as const;
export const URGENCY_LEVELS = [{
  value: 'laag',
  label: 'Laag - Planning voor de toekomst',
  color: 'bg-green-500'
}, {
  value: 'normaal',
  label: 'Normaal - Binnen enkele weken',
  color: 'bg-blue-500'
}, {
  value: 'hoog',
  label: 'Hoog - Zo snel mogelijk',
  color: 'bg-yellow-500'
}, {
  value: 'spoed',
  label: 'Spoed - Directe behoefte',
  color: 'bg-red-500'
}] as const;
// Helper functions to get labels
const getServiceTypeLabel = (id: string) => SERVICE_TYPES.find(s => s.id === id)?.label ?? id;
const getUrgencyLabel = (val: string) => URGENCY_LEVELS.find(u => u.value === val)?.label ?? val;
// Helper function to generate a random math question
const generateMathCaptcha = () => {
  const num1 = Math.floor(Math.random() * 10) + 1;
  const num2 = Math.floor(Math.random() * 10) + 1;
  return {
    question: `${num1} + ${num2} = ?`,
    answer: (num1 + num2).toString()
  };
};
// Generate a unique form token
const generateFormToken = () => {
  return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
};
// Check if the email is potentially suspicious
const isValidEmail = email => {
  // Basic email format validation
  const emailRegex = /^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$/;
  if (!emailRegex.test(email)) return false;
  // Check for common spam domains (this list should be maintained)
  const suspiciousDomains = ['temp-mail.org', 'guerrillamail.com', 'mailinator.com', 'tempmail.com', 'yopmail.com', 'throwawaymail.com'];
  const domain = email.split('@')[1];
  return !suspiciousDomains.includes(domain);
};
// Check for suspicious patterns in message content
const containsSuspiciousContent = message => {
  const suspiciousPatterns = [
  // allow only your own domain in links
  /https?:\/\/(?!saensebeveiliging\.nl)/i, /\b(viagra|cialis|buy now|casino|lottery|prize|winner)\b/i, /\b(crypto|bitcoin|ethereum|wallet|investment opportunity)\b/i];
  return suspiciousPatterns.some(pattern => pattern.test(message));
};
// Time threshold for "filled too fast" detection - reduced for testing
const MIN_FILL_TIME_MS = typeof window !== 'undefined' && window.location.hostname === 'localhost' ? 1500 // 1.5 seconds in development
: 5000; // 5 seconds in production
// Rate limit settings - increased for testing
const RATE_LIMIT_COUNT = typeof window !== 'undefined' && window.location.hostname === 'localhost' ? 20 // 20 submissions in development
: 3; // 3 submissions in production
// Create an HTML email template for the contact form submission
const createEmailTemplate = data => {
  const {
    name,
    email,
    phone,
    company,
    serviceType,
    urgency,
    message,
    newsletter,
    date,
    metadata
  } = data;
  const styles = `
    body { margin:0; padding:0; background:#ffffff; color:#111111;
           font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Arial,sans-serif; line-height:1.5; }
    .container { max-width:640px; margin:0 auto; border:1px solid #e5e5e5; }
    .header { padding:16px 20px; border-bottom:2px solid #000000; text-align:left; }
    .header h1 { margin:0; font-size:20px; font-weight:700; color:#000000; }
    .header p { margin:6px 0 0 0; font-size:13px; color:#444444; }
    .section { padding:16px 20px; border-bottom:1px solid #eeeeee; }
    .section:last-child { border-bottom:none; }
    .section-title { font-size:14px; font-weight:700; margin:0 0 10px 0; color:#000000; }
    .field { margin:6px 0; font-size:14px; }
    .field-label { display:inline-block; width:140px; color:#444444; font-weight:600; }
    .field-value { display:inline-block; color:#111111; }
    .message-box { margin-top:8px; padding:12px; background:#fafafa; border-left:3px solid #000000; font-size:14px; white-space:pre-wrap; }
    .metadata { margin-top:8px; padding:12px; background:#fafafa; border:1px solid #eeeeee; font-size:12px; color:#555555; }
    .pill { display:inline-block; padding:2px 8px; font-size:12px; font-weight:700; color:#ffffff; background:#000000; border-radius:9999px; text-transform:none; }
    .footer { padding:14px 20px; background:#ffffff; border-top:1px solid #e5e5e5; font-size:12px; color:#666666; text-align:center; }
    a { color:#000000; text-decoration:underline; }
  `;
  return `
<!DOCTYPE html>
<html lang="nl">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Nieuw contactformulier bericht</title>
 <style>
${styles}
</style>

</head>
<body>
  <div class="container">
    <div class="header">
      <h1>Nieuw Contactformulier Bericht</h1>
      <p>${date}</p>
    </div>
    <div class="section">
      <div class="section-title">Afzender Informatie</div>
      <div class="field"><span class="field-label">Naam:</span><span class="field-value">${name}</span></div>
      <div class="field"><span class="field-label">E-mail:</span><span class="field-value">${email}</span></div>
      ${phone ? `<div class="field"><span class="field-label">Telefoon:</span><span class="field-value">${phone}</span></div>` : ''}
      ${company ? `<div class="field"><span class="field-label">Bedrijf:</span><span class="field-value">${company}</span></div>` : ''}
    </div>
    <div class="section">
      <div class="section-title">Aanvraag Details</div>
      <div class="field"><span class="field-label">Type dienst:</span><span class="field-value">${serviceType}</span></div>
      <div class="field"><span class="field-label">Urgentie:</span><span class="field-value"><span class="pill">${urgency}</span></span></div>
      <div class="field"><span class="field-label">Nieuwsbrief:</span><span class="field-value">${newsletter}</span></div>
    </div>
    <div class="section">
      <div class="section-title">Bericht</div>
      <div class="message-box">
        ${message.replace(/\n/g, '<br>')}
      </div>
    </div>
    <div class="section">
      <div class="section-title">Meta</div>
      <div class="metadata">
        <div><strong>Verzonden vanaf:</strong> ${metadata.url}</div>
        <div><strong>IP-adres:</strong> ${metadata.ip}</div>
        <div><strong>Website:</strong> saensebeveiliging.nl</div>
        <div><strong>Datum en tijd:</strong> ${date}</div>
        <div><strong>Form ID:</strong> ${metadata.formId}</div>
      </div>
    </div>
    <div class="footer">
      <p>Dit bericht is verzonden via het contactformulier op saensebeveiliging.nl</p>
      <p>Mede mogelijk gemaakt door <a href="https://radux.nl">Radux.nl</a></p>
    </div>
  </div>
</body>
</html>
  `;
};
// CORS-safe send function with x-www-form-urlencoded format
const sendToMake = async formData => {
  console.log('Preparing form data for webhook');
  const webhookUrl = 'https://hook.eu2.make.com/wvk51vjx7aqx7v6klf9zgdhvp536slox';
  // Get user's IP address if available
  const ipAddress = typeof window !== 'undefined' ? window.userIP || 'Niet beschikbaar' : 'Niet beschikbaar';
  // Get current URL
  const currentUrl = typeof window !== 'undefined' ? window.location.href : 'saensebeveiliging.nl';
  // Get service type label using the module-scoped helper function
  const serviceTypeLabel = getServiceTypeLabel(formData.serviceType);
  // Get urgency level label using the module-scoped helper function
  const urgencyLabel = getUrgencyLabel(formData.urgency);
  // Format current date and time
  const formattedDate = new Date().toLocaleString('nl-NL', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
  // Create HTML email template
  const html = createEmailTemplate({
    name: formData.name,
    email: formData.email,
    phone: formData.phone || '',
    company: formData.company || '',
    serviceType: serviceTypeLabel,
    urgency: urgencyLabel,
    message: formData.message,
    newsletter: formData.newsletter ? 'Ja' : 'Nee',
    date: formattedDate,
    metadata: {
      ip: ipAddress,
      url: currentUrl,
      formId: formData.formToken
    }
  });
  // FLAT payload (no nested objects) for URLSearchParams
  const payload = {
    email_to: 'contact@saensebeveiliging.nl',
    email_replyTo: formData.email,
    email_subject: `Nieuwe beveiligingsaanvraag: ${serviceTypeLabel} (${urgencyLabel})`,
    sender_name: formData.name,
    sender_email: formData.email,
    sender_phone: formData.phone || '',
    sender_company: formData.company || '',
    request_serviceType: serviceTypeLabel,
    request_urgency: urgencyLabel,
    request_message: formData.message,
    request_newsletter: formData.newsletter ? 'Ja' : 'Nee',
    meta_date: formattedDate,
    meta_ip: ipAddress,
    meta_url: currentUrl,
    meta_formId: formData.formToken,
    meta_userAgent: typeof window !== 'undefined' ? window.navigator.userAgent : 'Unknown',
    htmlContent: html // Complete HTML email body
  };
  // Convert to URL-encoded string
  const body = new URLSearchParams(payload).toString();
  console.log('Sending URL-encoded data to webhook');
  const response = await fetch(webhookUrl, {
    method: 'POST',
    headers: {
      // Simple request -> no preflight with this content type
      'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
    },
    body,
    mode: 'cors'
  });
  console.log('Webhook response status:', response.status);
  if (!response.ok) {
    throw new Error(`Webhook error: ${response.status} ${response.statusText}`);
  }
  return response;
};
export function ContactSection() {
  const initialCaptcha = generateMathCaptcha();
  // Create a submissions counter in localStorage if it doesn't exist
  useEffect(() => {
    if (!localStorage.getItem('formSubmissionCount')) {
      localStorage.setItem('formSubmissionCount', '0');
    }
    if (!localStorage.getItem('lastSubmissionTime')) {
      localStorage.setItem('lastSubmissionTime', '0');
    }
  }, []);
  const initialFormData = {
    name: '',
    email: '',
    phone: '',
    company: '',
    serviceType: 'objectbeveiliging',
    urgency: 'normaal',
    message: '',
    newsletter: false,
    // Anti-spam fields with better naming
    hp_timestamp: '',
    hp_reference: '',
    captcha: '',
    captchaQuestion: initialCaptcha.question,
    captchaAnswer: initialCaptcha.answer,
    formStartTime: Date.now(),
    formToken: generateFormToken(),
    acceptTerms: false // Terms acceptance checkbox
  };
  const [formData, setFormData] = useState(initialFormData);
  const [submitted, setSubmitted] = useState(false);
  const [focused, setFocused] = useState('');
  const [formStep, setFormStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState({
    email: '',
    captcha: '',
    message: '',
    general: ''
  });
  const handleChange = e => {
    const {
      name,
      value,
      type,
      checked
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear errors when user makes changes
    if (Object.keys(formErrors).includes(name)) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };
  const handleFocus = field => {
    setFocused(field);
  };
  const handleBlur = () => {
    setFocused('');
  };
  // Reset form with proper captcha generation
  const resetForm = () => {
    const newCaptcha = generateMathCaptcha();
    setFormData({
      ...initialFormData,
      captchaQuestion: newCaptcha.question,
      captchaAnswer: newCaptcha.answer,
      formStartTime: Date.now(),
      formToken: generateFormToken()
    });
    setFormStep(0);
  };
  // Validate form step and proceed to next
  const validateAndProceed = nextStep => {
    // Step 1 validation (personal info)
    if (formStep === 0) {
      // Validate email
      if (!isValidEmail(formData.email)) {
        setFormErrors(prev => ({
          ...prev,
          email: 'Vul een geldig e-mailadres in'
        }));
        return;
      }
      // Validate captcha
      if (formData.captcha !== formData.captchaAnswer) {
        setFormErrors(prev => ({
          ...prev,
          captcha: 'Het antwoord is niet correct'
        }));
        return;
      }
    }
    // All validations passed, proceed to next step
    setFormStep(nextStep);
  };
  const handleSubmit = async e => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      // Check for honeypot fields (should be empty)
      if (formData.hp_timestamp || formData.hp_reference) {
        console.error('Honeypot triggered:', {
          hp_timestamp: formData.hp_timestamp,
          hp_reference: formData.hp_reference
        });
        setFormErrors({
          ...formErrors,
          general: 'Ongeldige invoer gedetecteerd.'
        });
        setIsSubmitting(false);
        return;
      }
      // Check if form was filled out too quickly (likely a bot)
      const formFillTime = Date.now() - formData.formStartTime;
      console.log(`Form fill time: ${formFillTime}ms, minimum: ${MIN_FILL_TIME_MS}ms`);
      if (formFillTime < MIN_FILL_TIME_MS) {
        // Less than threshold time
        setFormErrors({
          ...formErrors,
          general: 'Formulier te snel ingevuld. Probeer het opnieuw.'
        });
        setIsSubmitting(false);
        return;
      }
      // Check for suspicious content
      if (containsSuspiciousContent(formData.message)) {
        setFormErrors({
          ...formErrors,
          message: 'Uw bericht bevat niet-toegestane inhoud'
        });
        setIsSubmitting(false);
        return;
      }
      // Rate limiting check - configurable limit per hour
      const submissionCount = parseInt(localStorage.getItem('formSubmissionCount') || '0');
      const lastSubmissionTime = parseInt(localStorage.getItem('lastSubmissionTime') || '0');
      const currentTime = Date.now();
      const oneHourInMs = 60 * 60 * 1000;
      // Reset counter if it's been more than an hour since last submission
      if (currentTime - lastSubmissionTime > oneHourInMs) {
        localStorage.setItem('formSubmissionCount', '1');
        localStorage.setItem('lastSubmissionTime', currentTime.toString());
      } else if (submissionCount >= RATE_LIMIT_COUNT) {
        // If more than limit submissions in the last hour
        setFormErrors({
          ...formErrors,
          general: 'Maximum aantal berichten bereikt. Probeer het later opnieuw.'
        });
        setIsSubmitting(false);
        return;
      } else {
        // Increment counter for submissions within the hour
        localStorage.setItem('formSubmissionCount', (submissionCount + 1).toString());
        localStorage.setItem('lastSubmissionTime', currentTime.toString());
      }
      try {
        // Use the enhanced email template send method
        await sendToMake(formData);
        // If we get here, the request was successful
        console.log('Webhook request completed successfully');
        // Add a small delay before showing success to make the transition feel more natural
        setTimeout(() => {
          setSubmitted(true);
          setIsSubmitting(false);
          // Reset form with proper captcha
          resetForm();
        }, 800);
        // Reset submission status after 5 seconds
        setTimeout(() => {
          setSubmitted(false);
        }, 5000);
      } catch (webhookError) {
        console.error('Error sending to webhook:', webhookError);
        throw webhookError; // Re-throw to be caught by the outer try/catch
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setFormErrors({
        ...formErrors,
        general: 'Er is een fout opgetreden bij het verzenden. Probeer het later opnieuw.'
      });
      setIsSubmitting(false);
    }
  };
  // Fixed animated label class with proper positioning
  const getLabelClass = field => {
    return `absolute left-14 transition-all duration-200 ${focused === field || formData[field] ? '-top-2.5 text-xs text-orange-500 bg-gray-800 px-2 z-10' : 'top-3 text-gray-400'}`;
  };
  // Input class with dynamic focus state
  const getInputClass = field => {
    const hasError = formErrors[field];
    return `w-full p-3 pl-14 bg-gray-700 border ${hasError ? 'border-red-500' : focused === field ? 'border-orange-500' : 'border-gray-600'} rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500`;
  };
  return <div className="bg-black py-16 md:py-20 border-t border-zinc-800" data-id="element-83">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-84">
        <h2 className="text-2xl md:text-3xl font-light mb-8 md:mb-10 text-center" data-id="element-85">
          Neem Contact Op
        </h2>
        <div className="grid md:grid-cols-2 gap-8 md:gap-10" data-id="element-86">
          {/* Contact Info */}
          <div className="bg-zinc-900 p-6 rounded-none border border-zinc-800 shadow-sm" data-id="element-87">
            <h3 className="text-xl font-medium mb-5 border-b border-zinc-800 pb-3 text-slate-300" data-id="element-88">
              Contact Informatie
            </h3>
            <div className="space-y-5" data-id="element-89">
              <div className="group" data-id="element-90">
                <div className="font-medium text-slate-400 text-sm mb-1" data-id="element-91">
                  Telefoon
                </div>
                <div className="text-lg group-hover:text-gray-200 transition-colors" data-id="element-92">
                  06 55987275
                </div>
              </div>
              <div className="group" data-id="element-93">
                <div className="font-medium text-slate-400 text-sm mb-1" data-id="element-94">
                  E-mail
                </div>
                <div className="text-lg group-hover:text-gray-200 transition-colors" data-id="element-95">
                  contact@saensebeveiliging.nl
                </div>
              </div>
            </div>
            <div className="mt-8 space-y-4" data-id="element-96">
              <div className="text-lg font-medium border-b border-zinc-800 pb-3 text-slate-300" data-id="element-97">
                Diensten
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-base" data-id="element-98">
                <div className="p-3 bg-black rounded-none" data-id="element-99">
                  Objectbeveiliging
                </div>
                <div className="p-3 bg-black rounded-none" data-id="element-100">
                  Winkelbeveiliging
                </div>
                <div className="p-3 bg-black rounded-none" data-id="element-101">Evenementen</div>
                <div className="p-3 bg-black rounded-none" data-id="element-102">
                  Toegangscontrole
                </div>
              </div>
            </div>
          </div>
          {/* Contact Form */}
          <div className="bg-zinc-900 p-6 rounded-none border border-zinc-800 shadow-sm" data-id="element-103">
            {submitted ? <div className="bg-black border border-zinc-800 p-6 rounded-none text-center h-full flex flex-col items-center justify-center animate-fadeIn" data-id="element-104">
                <p className="text-gray-200 font-medium text-xl mb-3" data-id="element-105">
                  Bedankt voor uw bericht
                </p>
                <p className="text-zinc-400 text-base" data-id="element-106">
                  We nemen zo spoedig mogelijk contact met u op.
                </p>
              </div> : <form onSubmit={handleSubmit} className="space-y-5" data-id="element-107">
                <h3 className="text-xl font-medium mb-5 border-b border-zinc-800 pb-3 text-slate-300" data-id="element-108">
                  Stuur een bericht
                </h3>
                {/* Honeypot fields */}
                <div style={{
              display: 'none'
            }} aria-hidden="true" data-id="element-109">
                  <label htmlFor="hp_timestamp" data-id="element-110">Timestamp</label>
                  <input type="text" id="hp_timestamp" name="hp_timestamp" tabIndex={-1} autoComplete="new-password" value={formData.hp_timestamp || ''} onChange={handleChange} data-id="element-111" />
                  <label htmlFor="hp_reference" data-id="element-112">Reference</label>
                  <input type="text" id="hp_reference" name="hp_reference" tabIndex={-1} autoComplete="new-password" value={formData.hp_reference || ''} onChange={handleChange} data-id="element-113" />
                </div>
                {/* Form token and time tracker */}
                <input type="hidden" name="formToken" value={formData.formToken} data-id="element-114" />
                <input type="hidden" name="formStartTime" value={formData.formStartTime} data-id="element-115" />
                {/* Progress Indicator */}
                <div className="flex justify-between mb-5" data-id="element-116">
                  <div className="flex space-x-2" data-id="element-117">
                    {[0, 1, 2].map(step => <button key={step} type="button" onClick={() => step <= formStep ? setFormStep(step) : validateAndProceed(step)} className={`w-2.5 h-2.5 rounded-full ${formStep >= step ? 'bg-slate-500' : 'bg-zinc-700'} transition-colors`} aria-label={`Step ${step + 1}`} disabled={step > formStep} data-id="element-118" />)}
                  </div>
                  <div className="text-sm text-zinc-400" data-id="element-119">
                    Stap {formStep + 1}/3
                  </div>
                </div>
                {/* Step 1: Basic Info */}
                <div className={formStep === 0 ? 'block' : 'hidden'} data-id="element-120">
                  {/* Name Field */}
                  <div className="mb-4" data-id="element-121">
                    <label htmlFor="name" className="block text-base font-medium text-zinc-300 mb-2" data-id="element-122">
                      Naam
                    </label>
                    <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} className="w-full p-3 bg-black border border-zinc-800 rounded-none focus:outline-none focus:border-slate-600 text-base" required maxLength={50} pattern="^[^<>]*$" data-id="element-123" />
                  </div>
                  {/* Email Field */}
                  <div className="mb-4" data-id="element-124">
                    <label htmlFor="email" className="block text-base font-medium text-zinc-300 mb-2" data-id="element-125">
                      E-mail
                    </label>
                    <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} className="w-full p-3 bg-black border border-zinc-800 rounded-none focus:outline-none focus:border-slate-600 text-base" required pattern="[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$" maxLength={100} data-id="element-126" />
                    {formErrors.email && <p className="text-red-500 text-sm mt-2" data-id="element-127">
                        {formErrors.email}
                      </p>}
                  </div>
                  {/* Phone Field */}
                  <div className="mb-4" data-id="element-128">
                    <label htmlFor="phone" className="block text-base font-medium text-zinc-300 mb-2" data-id="element-129">
                      Telefoon
                    </label>
                    <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} className="w-full p-3 bg-black border border-zinc-800 rounded-none focus:outline-none focus:border-slate-600 text-base" pattern="^[0-9+\-\s()]*$" maxLength={20} data-id="element-130" />
                  </div>
                  {/* Captcha */}
                  <div className="mb-4" data-id="element-131">
                    <label htmlFor="captcha" className="block text-base font-medium text-zinc-300 mb-2" data-id="element-132">
                      Verificatie: {formData.captchaQuestion}
                    </label>
                    <input type="text" id="captcha" name="captcha" value={formData.captcha} onChange={handleChange} className="w-full p-3 bg-black border border-zinc-800 rounded-none focus:outline-none focus:border-slate-600 text-base" placeholder="Vul het antwoord in" required inputMode="numeric" maxLength={3} data-id="element-133" />
                    {formErrors.captcha && <p className="text-red-500 text-sm mt-2" data-id="element-134">
                        {formErrors.captcha}
                      </p>}
                  </div>
                  <button type="button" onClick={() => validateAndProceed(1)} className="w-full mt-4 py-3 bg-slate-800 hover:bg-slate-900 text-white font-normal text-base rounded-none transition-colors" disabled={isSubmitting} data-id="element-135">
                    Volgende
                  </button>
                </div>
                {/* Step 2: Service Details */}
                <div className={formStep === 1 ? 'block' : 'hidden'} data-id="element-136">
                  {/* Service Type Field */}
                  <div className="space-y-3 mb-5" data-id="element-137">
                    <label className="block text-base font-medium text-zinc-300" data-id="element-138">
                      Type dienst
                    </label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-139">
                      {SERVICE_TYPES.map(service => <label key={service.id} className={`flex items-center p-3 gap-2 border rounded-none cursor-pointer transition-all text-base ${formData.serviceType === service.id ? 'border-slate-600 bg-black' : 'border-zinc-800 hover:border-zinc-700'}`} data-id="element-140">
                          <input type="radio" name="serviceType" value={service.id} checked={formData.serviceType === service.id} onChange={handleChange} className="sr-only" data-id="element-141" />
                          <span data-id="element-142">{service.label}</span>
                        </label>)}
                    </div>
                  </div>
                  {/* Urgency Field */}
                  <div className="space-y-3" data-id="element-143">
                    <label className="block text-base font-medium text-zinc-300" data-id="element-144">
                      Urgentie
                    </label>
                    <div className="space-y-3" data-id="element-145">
                      {URGENCY_LEVELS.map(level => <label key={level.value} className={`flex items-center p-3 gap-3 border rounded-none cursor-pointer transition-all text-base ${formData.urgency === level.value ? 'border-slate-600 bg-black' : 'border-zinc-800 hover:border-zinc-700'}`} data-id="element-146">
                          <input type="radio" name="urgency" value={level.value} checked={formData.urgency === level.value} onChange={handleChange} className="sr-only" data-id="element-147" />
                          <div className={`w-3 h-3 rounded-full ${level.color}`} data-id="element-148"></div>
                          <span data-id="element-149">{level.label}</span>
                        </label>)}
                    </div>
                  </div>
                  <div className="flex justify-between mt-5 space-x-3" data-id="element-150">
                    <button type="button" onClick={() => setFormStep(0)} className="py-3 px-4 bg-zinc-800 hover:bg-zinc-700 text-white font-normal text-base rounded-none transition-colors" data-id="element-151">
                      Terug
                    </button>
                    <button type="button" onClick={() => validateAndProceed(2)} className="flex-grow py-3 bg-slate-800 hover:bg-slate-900 text-white font-normal text-base rounded-none transition-colors" data-id="element-152">
                      Volgende
                    </button>
                  </div>
                </div>
                {/* Step 3: Message */}
                <div className={formStep === 2 ? 'block' : 'hidden'} data-id="element-153">
                  {/* Message Field */}
                  <div className="mb-4" data-id="element-154">
                    <label className="block text-base font-medium text-zinc-300 mb-2" data-id="element-155">
                      Uw bericht
                    </label>
                    <textarea id="message" name="message" value={formData.message} onChange={handleChange} rows={4} className="w-full p-3 bg-black border border-zinc-800 rounded-none focus:outline-none focus:border-slate-600 text-base" placeholder="Beschrijf uw beveiligingsbehoefte of vraag..." required maxLength={1000} data-id="element-156"></textarea>
                    {formErrors.message && <p className="text-red-500 text-sm mt-2" data-id="element-157">
                        {formErrors.message}
                      </p>}
                  </div>
                  {/* Newsletter Checkbox */}
                  <div className="flex items-center space-x-3 p-3 bg-black rounded-none mb-3" data-id="element-158">
                    <input type="checkbox" id="newsletter" name="newsletter" checked={formData.newsletter} onChange={handleChange} className="h-5 w-5 text-slate-600 border-zinc-600 rounded-none" data-id="element-159" />
                    <label htmlFor="newsletter" className="text-base text-zinc-300 cursor-pointer" data-id="element-160">
                      Houd mij op de hoogte
                    </label>
                  </div>
                  {/* Terms acceptance */}
                  <div className="flex items-center space-x-3 p-3 bg-black rounded-none mb-3" data-id="element-161">
                    <input type="checkbox" id="acceptTerms" name="acceptTerms" checked={formData.acceptTerms} onChange={handleChange} className="h-5 w-5 text-slate-600 border-zinc-600 rounded-none" required data-id="element-162" />
                    <label htmlFor="acceptTerms" className="text-base text-zinc-300 cursor-pointer" data-id="element-163">
                      Ik ben geen robot en ga akkoord met de voorwaarden
                    </label>
                  </div>
                  {formErrors.general && <div className="p-3 bg-red-500/10 border border-red-500 rounded-none mt-4" data-id="element-164">
                      <p className="text-red-300 text-sm" data-id="element-165">
                        {formErrors.general}
                      </p>
                    </div>}
                  <div className="flex justify-between mt-5 space-x-3" data-id="element-166">
                    <button type="button" onClick={() => setFormStep(1)} className="py-3 px-4 bg-zinc-800 hover:bg-zinc-700 text-white font-normal text-base rounded-none transition-colors" data-id="element-167">
                      Terug
                    </button>
                    <button type="submit" className="flex-grow py-3 bg-slate-800 hover:bg-slate-900 text-white font-normal text-base rounded-none transition-colors relative overflow-hidden" disabled={isSubmitting} data-id="element-168">
                      {isSubmitting ? <div className="flex items-center justify-center" data-id="element-169">
                          <div className="animate-spin mr-2 h-5 w-5 border-t-2 border-b-2 border-white rounded-full" data-id="element-170"></div>
                          <span data-id="element-171">Verzenden...</span>
                        </div> : <span data-id="element-172">Verstuur</span>}
                      {isSubmitting && <div className="absolute bottom-0 left-0 h-1 bg-white animate-progress" data-id="element-173"></div>}
                    </button>
                  </div>
                </div>
              </form>}
          </div>
        </div>
      </div>
    </div>;
}